import { IEventParameterFilter } from '@faire/web-api--source/indigofair/data/IEventParameterFilter';

export namespace IEventSchema {
  export enum Action {
    ACTION_UNKNOWN = 'ACTION_UNKNOWN',
    CLICK = 'CLICK',
    VIEW = 'VIEW',
    SCROLL = 'SCROLL',
    HOVER = 'HOVER',
    SUBMIT = 'SUBMIT',
    KEYBOARD_INPUT = 'KEYBOARD_INPUT',
    TIMING = 'TIMING',
    DISMISS = 'DISMISS',
    ERROR = 'ERROR',
    SUCCESS = 'SUCCESS',
  }

  export namespace ITokenParameter {
    /**
     * Return an instance of ITokenParameter with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<ITokenParameter>
    ): ITokenParameter =>
      ({
        ...(partial ?? {}),
      }) as ITokenParameter;
  }

  export interface ITokenParameter {
    token: string | undefined;
    label: string | undefined;
    is_dimension: boolean | undefined;
    default_value: string | undefined;
    report_distribution_metric: boolean | undefined;
  }

  /**
   * The platform that the event schema is targeted to
   */
  export enum Platform {
    PLATFORM_UNKNOWN = 'PLATFORM_UNKNOWN',
    WEB = 'WEB',
    MOBILE = 'MOBILE',
  }

  /**
   * The customer that the event schema is targeted to
   */
  export enum App {
    APP_UNKNOWN = 'APP_UNKNOWN',
    BRAND = 'BRAND',
    RETAILER = 'RETAILER',
  }

  export enum Priority {
    PRIORITY_UNKNOWN = 'PRIORITY_UNKNOWN',
    CRITICAL = 'CRITICAL',
    NORMAL = 'NORMAL',
    LOW = 'LOW',
    EXTRA_LOW = 'EXTRA_LOW',
  }

  export namespace IPriorityConfig {
    /**
     * Return an instance of IPriorityConfig with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IPriorityConfig>
    ): IPriorityConfig =>
      ({
        ...(partial ?? {}),
      }) as IPriorityConfig;
  }

  export interface IPriorityConfig {
    priority: keyof typeof IEventSchema.Priority | undefined;
    /**
     * Service Level Agreement (SLA) for the average completion time P50.
     */
    sla_p50_ms: number | undefined;
    /**
     * Service Level Agreement (SLA) for the 95th quantile completion time.
     */
    sla_p95_ms: number | undefined;
  }

  export namespace IServiceLevelConstraint {
    /**
     * Return an instance of IServiceLevelConstraint with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IServiceLevelConstraint>
    ): IServiceLevelConstraint =>
      ({
        parameter_filters: [],
        ...(partial ?? {}),
      }) as IServiceLevelConstraint;
  }

  export interface IServiceLevelConstraint {
    parameter_filters: Array<IEventParameterFilter>;
    sla_constraint: IEventSchema.IPriorityConfig | undefined;
  }

  /**
   * The category that the event belongs to in terms of types of events.
   * To avoid collisions with the Action enums, all values are prefixed with EVENT_CATEGORY.
   */
  export enum EventCategory {
    EVENT_CATEGORY_UNKNOWN = 'EVENT_CATEGORY_UNKNOWN',
    EVENT_CATEGORY_CLICK = 'EVENT_CATEGORY_CLICK',
    EVENT_CATEGORY_TEXT_ENTRY = 'EVENT_CATEGORY_TEXT_ENTRY',
    EVENT_CATEGORY_ERROR = 'EVENT_CATEGORY_ERROR',
    EVENT_CATEGORY_SCROLL = 'EVENT_CATEGORY_SCROLL',
    EVENT_CATEGORY_API = 'EVENT_CATEGORY_API',
    EVENT_CATEGORY_EMAIL = 'EVENT_CATEGORY_EMAIL',
    EVENT_CATEGORY_SEARCH = 'EVENT_CATEGORY_SEARCH',
    EVENT_CATEGORY_IMPRESSION = 'EVENT_CATEGORY_IMPRESSION',
    /**
     * Other is distinct from UNKNOWN in that it was chosen intentionally.
     */
    EVENT_CATEGORY_OTHER = 'EVENT_CATEGORY_OTHER',
    EVENT_CATEGORY_PAGELOAD = 'EVENT_CATEGORY_PAGELOAD',
    EVENT_CATEGORY_EXPORT = 'EVENT_CATEGORY_EXPORT',
    EVENT_CATEGORY_IMPORT = 'EVENT_CATEGORY_IMPORT',
  }

  /**
   * The list of possible Action Surfaces as determined by the Canonical Events Mapping audit.
   * See https://www.notion.so/faire/Canonical-Events-Mapping-Config-66b58762f603434ca7f4062dff1e526d
   */
  export enum ActionSurface {
    ACTION_SURFACE_UNKNOWN = 'ACTION_SURFACE_UNKNOWN',
    ACTION_SURFACE_ACCOUNT_NAVIGATION = 'ACTION_SURFACE_ACCOUNT_NAVIGATION',
    ACTION_SURFACE_ACCOUNT_SETTINGS = 'ACTION_SURFACE_ACCOUNT_SETTINGS',
    ACTION_SURFACE_ADMIN = 'ACTION_SURFACE_ADMIN',
    ACTION_SURFACE_ADS_MANAGER = 'ACTION_SURFACE_ADS_MANAGER',
    ACTION_SURFACE_ALERT_BANNER = 'ACTION_SURFACE_ALERT_BANNER',
    ACTION_SURFACE_APPAREL_BRANDS = 'ACTION_SURFACE_APPAREL_BRANDS',
    ACTION_SURFACE_APPAREL_DISCOVERY = 'ACTION_SURFACE_APPAREL_DISCOVERY',
    ACTION_SURFACE_BOARDS = 'ACTION_SURFACE_BOARDS',
    ACTION_SURFACE_BRAND_ANALYTICS = 'ACTION_SURFACE_BRAND_ANALYTICS',
    ACTION_SURFACE_BRAND_CRM = 'ACTION_SURFACE_BRAND_CRM',
    ACTION_SURFACE_BRAND_CUSTOMERS = 'ACTION_SURFACE_BRAND_CUSTOMERS',
    ACTION_SURFACE_BRAND_MARKETING = 'ACTION_SURFACE_BRAND_MARKETING',
    ACTION_SURFACE_BRAND_MARKETING_CAMPAIGNS = 'ACTION_SURFACE_BRAND_MARKETING_CAMPAIGNS',
    ACTION_SURFACE_BRAND_MOBILE_APP = 'ACTION_SURFACE_BRAND_MOBILE_APP',
    ACTION_SURFACE_BRAND_NUDGES = 'ACTION_SURFACE_BRAND_NUDGES',
    ACTION_SURFACE_BRAND_ONBOARDING = 'ACTION_SURFACE_BRAND_ONBOARDING',
    ACTION_SURFACE_BRAND_ORDERS = 'ACTION_SURFACE_BRAND_ORDERS',
    ACTION_SURFACE_BRAND_PAGE = 'ACTION_SURFACE_BRAND_PAGE',
    ACTION_SURFACE_BRAND_PORTAL_HOMEPAGE = 'ACTION_SURFACE_BRAND_PORTAL_HOMEPAGE',
    ACTION_SURFACE_BRAND_REFERRALS = 'ACTION_SURFACE_BRAND_REFERRALS',
    ACTION_SURFACE_BUY_IT_AGAIN = 'ACTION_SURFACE_BUY_IT_AGAIN',
    ACTION_SURFACE_CAREERS_PAGE = 'ACTION_SURFACE_CAREERS_PAGE',
    ACTION_SURFACE_CART = 'ACTION_SURFACE_CART',
    ACTION_SURFACE_CATEGORIES = 'ACTION_SURFACE_CATEGORIES',
    ACTION_SURFACE_CATEGORY_HOMEPAGE = 'ACTION_SURFACE_CATEGORY_HOMEPAGE',
    ACTION_SURFACE_CATNAV = 'ACTION_SURFACE_CATNAV',
    ACTION_SURFACE_CHECKOUT = 'ACTION_SURFACE_CHECKOUT',
    ACTION_SURFACE_CHECKOUT_SUMMARY = 'ACTION_SURFACE_CHECKOUT_SUMMARY',
    ACTION_SURFACE_CLIENT_EVENTS = 'ACTION_SURFACE_CLIENT_EVENTS',
    ACTION_SURFACE_COLLECTIONS = 'ACTION_SURFACE_COLLECTIONS',
    ACTION_SURFACE_CORE_COLLECTIONS = 'ACTION_SURFACE_CORE_COLLECTIONS',
    ACTION_SURFACE_DISCOVER_PAGE = 'ACTION_SURFACE_DISCOVER_PAGE',
    ACTION_SURFACE_DISCOVERY_RECOMMENDATIONS = 'ACTION_SURFACE_DISCOVERY_RECOMMENDATIONS',
    ACTION_SURFACE_FAIRE_CONTENT = 'ACTION_SURFACE_FAIRE_CONTENT',
    ACTION_SURFACE_FAIRE_DIRECT = 'ACTION_SURFACE_FAIRE_DIRECT',
    ACTION_SURFACE_FAIRE_DIRECT_REWARDS = 'ACTION_SURFACE_FAIRE_DIRECT_REWARDS',
    ACTION_SURFACE_FAIRESYNC = 'ACTION_SURFACE_FAIRESYNC',
    ACTION_SURFACE_FASHION_WEEK = 'ACTION_SURFACE_FASHION_WEEK',
    ACTION_SURFACE_FLASH_SALES = 'ACTION_SURFACE_FLASH_SALES',
    ACTION_SURFACE_HELP_CENTER = 'ACTION_SURFACE_HELP_CENTER',
    ACTION_SURFACE_HOME_FEED = 'ACTION_SURFACE_HOME_FEED',
    ACTION_SURFACE_HOMEPAGE = 'ACTION_SURFACE_HOMEPAGE',
    ACTION_SURFACE_INSIDER = 'ACTION_SURFACE_INSIDER',
    ACTION_SURFACE_INVENTORY = 'ACTION_SURFACE_INVENTORY',
    ACTION_SURFACE_INVOICES = 'ACTION_SURFACE_INVOICES',
    ACTION_SURFACE_LINE_SHEETS = 'ACTION_SURFACE_LINE_SHEETS',
    ACTION_SURFACE_MARKETPLACE = 'ACTION_SURFACE_MARKETPLACE',
    ACTION_SURFACE_MARKETS = 'ACTION_SURFACE_MARKETS',
    ACTION_SURFACE_MESSAGES = 'ACTION_SURFACE_MESSAGES',
    ACTION_SURFACE_MODAL = 'ACTION_SURFACE_MODAL',
    ACTION_SURFACE_MY_BRANDS = 'ACTION_SURFACE_MY_BRANDS',
    ACTION_SURFACE_MY_ORDERS = 'ACTION_SURFACE_MY_ORDERS',
    ACTION_SURFACE_OPEN_WITH_FAIRE = 'ACTION_SURFACE_OPEN_WITH_FAIRE',
    ACTION_SURFACE_ORDER_DETAILS = 'ACTION_SURFACE_ORDER_DETAILS',
    /**
     * Other is distinct from UNKNOWN in that it was chosen intentionally.
     */
    ACTION_SURFACE_OTHER = 'ACTION_SURFACE_OTHER',
    ACTION_SURFACE_PAYMENTS = 'ACTION_SURFACE_PAYMENTS',
    ACTION_SURFACE_POS_INTEGRATION = 'ACTION_SURFACE_POS_INTEGRATION',
    ACTION_SURFACE_POST_ORDER = 'ACTION_SURFACE_POST_ORDER',
    ACTION_SURFACE_PREORDERS = 'ACTION_SURFACE_PREORDERS',
    ACTION_SURFACE_PRICING_PARITY = 'ACTION_SURFACE_PRICING_PARITY',
    ACTION_SURFACE_PRODUCT = 'ACTION_SURFACE_PRODUCT',
    ACTION_SURFACE_PRODUCT_PAGE = 'ACTION_SURFACE_PRODUCT_PAGE',
    ACTION_SURFACE_PRODUCT_TILE = 'ACTION_SURFACE_PRODUCT_TILE',
    ACTION_SURFACE_PROMOTIONAL_EVENTS = 'ACTION_SURFACE_PROMOTIONAL_EVENTS',
    ACTION_SURFACE_R2B_REFERRALS = 'ACTION_SURFACE_R2B_REFERRALS',
    ACTION_SURFACE_RECOMMENDATIONS = 'ACTION_SURFACE_RECOMMENDATIONS',
    ACTION_SURFACE_RETAILER_BRANDS_MANAGEMENT = 'ACTION_SURFACE_RETAILER_BRANDS_MANAGEMENT',
    ACTION_SURFACE_RETAILER_MARKETING = 'ACTION_SURFACE_RETAILER_MARKETING',
    ACTION_SURFACE_RETAILER_ORDER_MANAGEMENT = 'ACTION_SURFACE_RETAILER_ORDER_MANAGEMENT',
    ACTION_SURFACE_RETAILER_PORTAL = 'ACTION_SURFACE_RETAILER_PORTAL',
    ACTION_SURFACE_RETAILERS = 'ACTION_SURFACE_RETAILERS',
    ACTION_SURFACE_SAMPLES = 'ACTION_SURFACE_SAMPLES',
    ACTION_SURFACE_SEARCH = 'ACTION_SURFACE_SEARCH',
    ACTION_SURFACE_SEARCH_BAR = 'ACTION_SURFACE_SEARCH_BAR',
    ACTION_SURFACE_SHOWROOM = 'ACTION_SURFACE_SHOWROOM',
    ACTION_SURFACE_TAXONOMY = 'ACTION_SURFACE_TAXONOMY',
    ACTION_SURFACE_TOP_SHOP = 'ACTION_SURFACE_TOP_SHOP',
    ACTION_SURFACE_TOP_BRAND = 'ACTION_SURFACE_TOP_BRAND',
    ACTION_SURFACE_VIDEOS = 'ACTION_SURFACE_VIDEOS',
    ACTION_SURFACE_BRAND_WIDGET = 'ACTION_SURFACE_BRAND_WIDGET',
  }

  /**
   * The list of possible Action Categories as determined by the Canonical Events Mapping audit.
   * See https://www.notion.so/faire/Canonical-Events-Mapping-Config-66b58762f603434ca7f4062dff1e526d
   */
  export enum ActionCategory {
    ACTION_CATEGORY_UNKNOWN = 'ACTION_CATEGORY_UNKNOWN',
    ACTION_CATEGORY_ADD = 'ACTION_CATEGORY_ADD',
    ACTION_CATEGORY_API = 'ACTION_CATEGORY_API',
    ACTION_CATEGORY_CART_ADD = 'ACTION_CATEGORY_CART_ADD',
    ACTION_CATEGORY_CLICK = 'ACTION_CATEGORY_CLICK',
    ACTION_CATEGORY_EDIT = 'ACTION_CATEGORY_EDIT',
    ACTION_CATEGORY_EMAIL = 'ACTION_CATEGORY_EMAIL',
    ACTION_CATEGORY_ERROR = 'ACTION_CATEGORY_ERROR',
    ACTION_CATEGORY_EXPORT = 'ACTION_CATEGORY_EXPORT',
    ACTION_CATEGORY_IMPRESSION = 'ACTION_CATEGORY_IMPRESSION',
    /**
     * Other is distinct from UNKNOWN in that it was chosen intentionally.
     */
    ACTION_CATEGORY_OTHER = 'ACTION_CATEGORY_OTHER',
    ACTION_CATEGORY_PAGELOAD = 'ACTION_CATEGORY_PAGELOAD',
    ACTION_CATEGORY_SCROLL = 'ACTION_CATEGORY_SCROLL',
    ACTION_CATEGORY_SEARCH = 'ACTION_CATEGORY_SEARCH',
    ACTION_CATEGORY_SUCCESS = 'ACTION_CATEGORY_SUCCESS',
    ACTION_CATEGORY_TEXT_ENTRY = 'ACTION_CATEGORY_TEXT_ENTRY',
  }

  /**
   * Return an instance of IEventSchema with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IEventSchema>): IEventSchema =>
    ({
      parameters: [],
      platforms: [],
      apps: [],
      trace_event_keys: [],
      source_event_keys: [],
      sla_constraints: [],
      ...(partial ?? {}),
    }) as IEventSchema;
}

export interface IEventSchema {
  /**
   * Populated on creation, not supplied by the client
   */
  event_key: string | undefined;
  /**
   * To be validated against ProductArea
   */
  product_area: string | undefined;
  action: keyof typeof IEventSchema.Action | undefined;
  name: string | undefined;
  description: string | undefined;
  deprecated: boolean | undefined;
  created_at: number | undefined;
  created_by: string | undefined;
  updated_at: number | undefined;
  updated_by: string | undefined;
  record_full_story: number | undefined;
  existing_normalized_path: string | undefined;
  deprecated_at: number | undefined;
  deprecated_by: string | undefined;
  parameters: Array<IEventSchema.ITokenParameter>;
  created_by_user_token: string | undefined;
  created_by_mdc_op: string | undefined;
  updated_by_user_token: string | undefined;
  updated_by_mdc_op: string | undefined;
  deprecated_by_user_token: string | undefined;
  deprecated_by_mdc_op: string | undefined;
  /**
   * Whether to emit an DataDog RUM action (in addition to sending the api/client-event request) for any RUM sessions.
   */
  real_time_tracking: boolean | undefined;
  created_by_user_email: string | undefined;
  /**
   * Name of the custom metric to emit in datadog for every api/client-event request.
   * Note that the metric will be emitted under the full path faire.pete.[datadogCustomMetric].
   */
  datadog_custom_metric: string | undefined;
  /**
   * Name of the event to emit to facebook-pixel (in addition to sending the api/client-event request) from the client.
   */
  facebook_pixel_event: string | undefined;
  /**
   * Name of the event to emit to Google Tag Manager (in addition to sending the api/client-event request)
   * from the client.
   */
  google_tag_manager_event: string | undefined;
  platforms: Array<keyof typeof IEventSchema.Platform>;
  apps: Array<keyof typeof IEventSchema.App>;
  /**
   * If this event schema is a source event, these are the trace events under it
   */
  trace_event_keys: Array<string>;
  /**
   * If this event schema is a trace event, this is its source events' reference
   */
  source_event_keys: Array<string>;
  /**
   * true if this event schema is a trace event.
   */
  is_trace_event: boolean | undefined;
  manual_overrides: boolean | undefined;
  /**
   * Name of the event to forward to braze (in addition to sending the api/client-event request) from the client.
   */
  braze_event_name: string | undefined;
  display_name: string | undefined;
  priority_config: IEventSchema.IPriorityConfig | undefined;
  sla_constraints: Array<IEventSchema.IServiceLevelConstraint>;
  bypass_session_recording: boolean | undefined;
  event_category: keyof typeof IEventSchema.EventCategory | undefined;
  /**
   * Whether or not this event should start an Open Telemetry trace.
   * This should only be set to true on parent events.
   */
  start_trace: boolean | undefined;
  /**
   * Whether or not this event should end an Open Telemetry trace.
   * This should only be set to true on PETE Trace events (i.e. child events).
   */
  end_trace: boolean | undefined;
  /**
   * Whether or not this event's actionCategory has been edited by a human yet.
   */
  action_surface_edited: boolean | undefined;
  /**
   * The Action Surface that this event belongs to.
   * See https://www.notion.so/faire/Canonical-Events-Mapping-Config-66b58762f603434ca7f4062dff1e526d
   */
  action_surface: keyof typeof IEventSchema.ActionSurface | undefined;
  /**
   * Whether or not this event's actionCategory has been edited by a human yet.
   */
  action_category_edited: boolean | undefined;
  /**
   * The Action Category that this event belongs to.
   * See https://www.notion.so/faire/Canonical-Events-Mapping-Config-66b58762f603434ca7f4062dff1e526d
   */
  action_category: keyof typeof IEventSchema.ActionCategory | undefined;
  /**
   * The name of the proto used to define the custom event schema.
   * Every unique event type should have its own unique schema.
   */
  event_proto_name: string | undefined;
}
